import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { finalizeCreditPurchase } from '../services/api';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { SEO } from '../components/SEO';

export const CreditSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addedCredits, setAddedCredits] = useState<number>(0);

  useEffect(() => {
    if (!sessionId) {
      setError("No session ID found.");
      setLoading(false);
      return;
    }

    const finalize = async () => {
      try {
        const res = await finalizeCreditPurchase(sessionId);
        if (res.success) {
            setAddedCredits(res.added || 20);
        } else {
            throw new Error(res.error || "Failed to finalize purchase");
        }
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Failed to verify purchase.");
      } finally {
        setLoading(false);
      }
    };

    finalize();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500">Verifying purchase...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
        <div className="max-w-md w-full bg-red-50 p-6 rounded-2xl text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-red-900 mb-2">Purchase Failed</h1>
            <p className="text-red-700 mb-6">{error}</p>
            <button 
                onClick={() => navigate('/')} 
                className="bg-white text-red-900 px-6 py-2 rounded-lg font-medium border border-red-200 hover:bg-red-50"
            >
                Return Home
            </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <SEO 
        title="Payment Successful"
        description="Thank you for your purchase."
      />
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center animate-in fade-in zoom-in duration-300">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Success!</h1>
          <p className="text-gray-600 mb-2">You have successfully purchased credits.</p>
          {addedCredits > 0 && (
             <p className="text-2xl font-bold text-blue-600 mb-6">+{addedCredits} Credits</p>
          )}
          <button 
            onClick={() => navigate('/')} 
            className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
              Start Creating
          </button>
      </div>
    </div>
  );
};
